import { ProjectConvertResponse } from "../../infra/api/service/project_api_convert";
import { FrontDate } from "./frontDate";
import { SearchHighlight } from "./search_highlight";

// 可動場所
export enum Place {
  Place_Unknown = 0,
  // 自社
  Place_InHouse = 1,
  // リモート
  Place_Remote = 2,
  // その他
  Place_Other = 3,
}

// 職種区分
export enum Occupation {
  Occupation_Unknown = 0,
  // 経営企画
  Occupation_CorporateBusinessPlanning = 1,
  // 新規事業開発
  Occupation_BusinessDevelopment = 2,
  // 営業
  Occupation_Sales = 3,
  // 広報
  Occupation_PublicRelations = 4,
  // IPO関連
  Occupation_IPO = 5,
  // Webマーケティング
  Occupation_WebMarketing = 6,
  // 人事
  Occupation_HumanResource = 7,
  // 人事（削除予定）8 => 7
  Occupation_HumanResource_Deprecate_1 = 8,
  // 人事（削除予定）9 => 7
  Occupation_HumanResource_Deprecate_2 = 9,
  // 人事（削除予定）10 => 7
  Occupation_HumanResource_Deprecate_3 = 10,
  // IPO関連（削除予定）11 => 5
  Occupation_IPO_Deprecate_1 = 11,
  // 経理/財務
  Occupation_Finance = 12,
  // 経理/財務（削除予定）13 => 12
  Occupation_Finance_Deprecate_1 = 13,
  // 経理（削除予定）14 => 12
  Occupation_Finance_Deprecate_2 = 14,
  // 法務
  Occupation_MinistryOfJustice = 15,
  // エンジニア
  Occupation_Engineer = 16,
  // デザイナー
  Occupation_Designer = 17,
  // その他
  Occupation_Other = 18,
  // 情報システム
  Occupation_InformationSystem = 19,
  // 研究者/教授
  Occupation_Professor = 20,
  // マーケティング
  Occupation_Marketing = 21,
}

// 稼働期間
export enum WorkingHourRange {
  WorkingHourRange_Unknown = 0,
  // 20~40時間
  WorkingHourRange_20_40_Hour = 1,
  // 40~80時間
  WorkingHourRange_40_80_Hour = 2,
  // 80~120時間
  WorkingHourRange_80_120_Hour = 3,
  // 120時間以上
  WorkingHourRange_Over_120_Hour = 4,
  // その他
  WorkingHourRange_Other = 5,
}

// 報酬目安
export enum PaymentRange {
  PaymentRange_Unknown = 0,
  // 10万円以下
  PaymentRange_Under_100000_Yen = 1,
  // 10~30万円
  PaymentRange_100000_300000_Yen = 2,
  // 30~50万円
  PaymentRange_300000_500000_Yen = 3,
  // 50~100万円
  PaymentRange_500000_1000000_Yen = 4,
  // 100万円以上
  PaymentRange_Over_1000000_Yen = 5,
}

// 稼働期間
export enum WorkingDuration {
  WorkingDuration_Unknown = 0,
  // 1ヶ月以内
  WorkingDuration_Under_1_Month = 1,
  // 1~3ヶ月以内
  WorkingDuration_1_3_Month = 2,
  // 3~6ヶ月
  WorkingDuration_3_6_Month = 3,
  // 6~12ヶ月
  WorkingDuration_6_12_Month = 4,
  // 12ヶ月以上
  WorkingDuration_Over_12_Month = 5,
}

// 案件ステータス
export enum Status {
  Status_Unknown = 0,
  // 公開
  Status_Open = 1,
  // 下書き
  Status_Draft = 2,
  // 公開終了
  Status_Close = 3,
  // 公開終了
  Status_Contracted = 4,
  // 限定公開
  Status_OnlyScout = 5,
}

// 案件
export class Project {
  public static from(from: ProjectConvertResponse): Project {
    return new Project(
      from.id,
      from.companyID,
      from.name,
      from.description,
      from.background,
      from.preferentialExperience,
      from.gsThumbnailURL,
      from.signedThumbnailURL,
      from.place,
      from.workingHourRange,
      from.paymentRange,
      from.workingDuration,
      from.occupations,
      from.status,
      from.operatingDate,
      from.isHideCompany,
      from.createdAt,
      from.highlights,
      from.shouldShowByContract,
      from.shouldShowByScout,
      from.messageRoomNum
    );
  }

  constructor(
    // 案件ID
    public readonly id: string,
    // 企業ID
    public readonly companyID: string,
    // 案件タイトル
    public readonly name: string,
    // 業務内容
    public readonly description: string,
    // 募集背景
    public readonly background: string,
    // 求める経験
    public readonly preferentialExperience: string,
    // 案件TOP画像
    public readonly gsThumbnailURL: string,
    public readonly signedThumbnailURL: string,
    // 可動場所
    public readonly place: Place,
    // 可動時間
    public readonly workingHourRange: WorkingHourRange,
    // 報酬目安
    public readonly paymentRange: PaymentRange,
    // 稼働期間
    public readonly workingDuration: WorkingDuration,
    // 職種区分
    public readonly occupations: Occupation[],
    // 案件ステータス
    public readonly status: Status,
    // 稼働希望年月日
    public readonly operatingDate: FrontDate,
    // company 非表示フラグ
    public readonly isHideCompany: boolean,
    // (タイムスタンプ)
    public readonly createdAt: FrontDate,

    public readonly highlights: SearchHighlight[],

    public readonly shouldShowByContract: boolean,
    public readonly shouldShowByScout: boolean,
    public readonly messageRoomNum: number
  ) {}

  public getOperatingDateString(): string {
    // YYYY-MM
    return `${this.operatingDate.year}-${this.operatingDate.month}`;
  }

  public getThumbnailURL(): string {
    if (this.signedThumbnailURL) {
      return this.signedThumbnailURL;
    } else {
      return this.gsThumbnailURL;
    }
  }

  public getPaymentRangeString(): string {
    switch (this.paymentRange) {
      case PaymentRange.PaymentRange_Under_100000_Yen:
        return "10万円以下";
      case PaymentRange.PaymentRange_100000_300000_Yen:
        return "10~30万円";
      case PaymentRange.PaymentRange_300000_500000_Yen:
        return "30~50万円";
      case PaymentRange.PaymentRange_500000_1000000_Yen:
        return "50~100万円";
      case PaymentRange.PaymentRange_Over_1000000_Yen:
        return "100万円以上";
      default:
        return "";
    }
  }

  public getWorkingHourRangeString(): string {
    switch (this.workingHourRange) {
      case WorkingHourRange.WorkingHourRange_20_40_Hour:
        return "20~40時間";
      case WorkingHourRange.WorkingHourRange_40_80_Hour:
        return "40~80時間";
      case WorkingHourRange.WorkingHourRange_80_120_Hour:
        return "80~120時間";
      case WorkingHourRange.WorkingHourRange_Over_120_Hour:
        return "120時間以上";
      default:
        return "";
    }
  }

  public statusString(): string {
    switch (this.status) {
      case Status.Status_Open:
        return "公開中";
      case Status.Status_Draft:
        return "下書き";
      case Status.Status_Close:
        return "公開終了";
      case Status.Status_Contracted:
        return "契約済み";
      case Status.Status_OnlyScout:
        return "限定公開";
      default:
        return "";
    }
  }
}



// WEBPACK FOOTER //
// ./src/domain/model/project.ts